/**
 * 全局通知系统 - 事件驱动方案
 * 用于在 React 组件树外（如拦截器中）触发提示信息
 * 通过事件系统与 App 组件中的 message 进行通信，避免主题上下文警告
 */

type NotificationType = "success" | "error" | "warning" | "info";

interface NotificationEvent {
  type: NotificationType;
  message: string;
}

// 创建自定义事件
const notificationEvent = new EventTarget();
const NOTIFICATION_EVENT = "app:notification";

/**
 * 订阅通知事件（在 App 组件中调用）
 * @param callback 回调函数，接收通知类型和消息
 * @returns 取消订阅函数
 */
export const subscribeNotification = (
  callback: (event: NotificationEvent) => void,
) => {
  const listener = (event: Event) => {
    if (event instanceof CustomEvent) {
      callback(event.detail);
    }
  };
  notificationEvent.addEventListener(NOTIFICATION_EVENT, listener);

  return () => {
    notificationEvent.removeEventListener(NOTIFICATION_EVENT, listener);
  };
};

/**
 * 发送通知事件（可在任何地方调用，包括拦截器）
 * @param type 通知类型
 * @param message 通知消息
 */
const emitNotification = (type: NotificationType, message: string) => {
  const event = new CustomEvent(NOTIFICATION_EVENT, {
    detail: { type, message },
  });
  notificationEvent.dispatchEvent(event);
};

/**
 * 显示成功提示
 * @param message 提示信息
 */
export const notifySuccess = (message: string) => {
  emitNotification("success", message);
};

/**
 * 显示错误提示
 * @param message 提示信息
 */
export const notifyError = (message: string) => {
  emitNotification("error", message);
};

/**
 * 显示警告提示
 * @param message 提示信息
 */
export const notifyWarning = (message: string) => {
  emitNotification("warning", message);
};

/**
 * 显示信息提示
 * @param message 提示信息
 */
export const notifyInfo = (message: string) => {
  emitNotification("info", message);
};
